import React from 'react';
import { motion } from 'framer-motion';
import './PrivacyPolicyPage.css';

const CookiePolicyPage = () => {
  return (
    <div className="policy-page">
      <section className="policy-hero">
        <div className="policy-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Cookie Policy</h1>
            <p className="last-updated">Last Updated: November 25, 2024</p>
          </motion.div>
        </div>
      </section>

      <section className="policy-content">
        <div className="policy-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="policy-section">
              <h2>1. What Are Cookies?</h2>
              <p>
                Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
              </p>
              <p>
                Cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. They enable us to improve our services and provide you with a better, more personalized experience.
              </p>
            </div>

            <div className="policy-section">
              <h2>2. How We Use Cookies</h2>
              <p>Dexova uses cookies for various purposes, including:</p>
              <ul>
                <li><strong>Essential Cookies:</strong> These cookies are necessary for the website to function properly and cannot be disabled in our systems.</li>
                <li><strong>Performance Cookies:</strong> These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.</li>
                <li><strong>Functional Cookies:</strong> These cookies enable enhanced functionality and personalization, such as remembering your preferences.</li>
                <li><strong>Targeting/Advertising Cookies:</strong> These cookies are used to deliver advertisements more relevant to you and your interests.</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>3. Types of Cookies We Use</h2>

              <h3>3.1 Strictly Necessary Cookies</h3>
              <p>
                These cookies are essential for you to browse our website and use its features. Without these cookies, services you have asked for cannot be provided.
              </p>
              <p><strong>Examples:</strong></p>
              <ul>
                <li>Session cookies for authentication</li>
                <li>Security cookies to prevent fraudulent activity</li>
                <li>Load balancing cookies to distribute requests</li>
              </ul>

              <h3>3.2 Performance and Analytics Cookies</h3>
              <p>
                These cookies collect information about how visitors use our website, such as which pages are visited most often and if error messages are displayed.
              </p>
              <p><strong>Examples:</strong></p>
              <ul>
                <li>Google Analytics cookies</li>
                <li>Page load time tracking</li>
                <li>Navigation pattern analysis</li>
                <li>Error tracking and reporting</li>
              </ul>

              <h3>3.3 Functionality Cookies</h3>
              <p>
                These cookies allow our website to remember choices you make and provide enhanced, more personal features.
              </p>
              <p><strong>Examples:</strong></p>
              <ul>
                <li>Language preferences</li>
                <li>Theme preferences (light/dark mode)</li>
                <li>User interface customizations</li>
                <li>Video player preferences</li>
              </ul>

              <h3>3.4 Targeting and Advertising Cookies</h3>
              <p>
                These cookies are used to deliver advertisements that are more relevant to you. They also limit the number of times you see an advertisement and help measure the effectiveness of advertising campaigns.
              </p>
              <p><strong>Examples:</strong></p>
              <ul>
                <li>Social media sharing cookies</li>
                <li>Remarketing cookies</li>
                <li>Interest-based advertising cookies</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>4. Third-Party Cookies</h2>
              <p>
                In addition to our own cookies, we may also use various third-party cookies to report usage statistics of our website and deliver advertisements.
              </p>
              <p><strong>Third-party services we use include:</strong></p>
              <ul>
                <li><strong>Google Analytics:</strong> For website traffic analysis and user behavior tracking</li>
                <li><strong>Google Ads:</strong> For delivering targeted advertisements</li>
                <li><strong>Social Media Platforms:</strong> For social sharing and integration (Facebook, Twitter, LinkedIn)</li>
                <li><strong>Content Delivery Networks:</strong> For improved website performance</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>5. How Long Do Cookies Last?</h2>
              <p>Cookies can be either "session" or "persistent" cookies:</p>
              <ul>
                <li><strong>Session Cookies:</strong> These cookies are temporary and are deleted when you close your browser. They help us track your activity during a single browsing session.</li>
                <li><strong>Persistent Cookies:</strong> These cookies remain on your device for a set period or until you delete them. They help us recognize you as a returning visitor and remember your preferences.</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>6. Managing Cookies</h2>
              <h3>6.1 Browser Settings</h3>
              <p>
                Most web browsers allow you to control cookies through their settings. You can typically:
              </p>
              <ul>
                <li>View what cookies are stored on your device</li>
                <li>Delete cookies individually or all at once</li>
                <li>Block third-party cookies</li>
                <li>Block all cookies from specific websites</li>
                <li>Block all cookies entirely</li>
              </ul>

              <h3>6.2 Consequences of Disabling Cookies</h3>
              <p>
                Please note that if you choose to disable cookies, some features of our website may not function properly. You may not be able to:
              </p>
              <ul>
                <li>Save your preferences</li>
                <li>Log in to your account</li>
                <li>Access personalized content</li>
                <li>Use certain interactive features</li>
              </ul>

              <h3>6.3 How to Control Cookies in Different Browsers</h3>
              <p><strong>Google Chrome:</strong></p>
              <ul>
                <li>Go to Settings → Privacy and security → Cookies and other site data</li>
              </ul>
              <p><strong>Mozilla Firefox:</strong></p>
              <ul>
                <li>Go to Options → Privacy & Security → Cookies and Site Data</li>
              </ul>
              <p><strong>Safari:</strong></p>
              <ul>
                <li>Go to Preferences → Privacy → Cookies and website data</li>
              </ul>
              <p><strong>Microsoft Edge:</strong></p>
              <ul>
                <li>Go to Settings → Cookies and site permissions</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>7. Do Not Track Signals</h2>
              <p>
                Some browsers include a "Do Not Track" (DNT) feature that signals to websites that you do not want to be tracked. Currently, there is no industry standard for how to respond to DNT signals. We do not currently respond to DNT browser signals or mechanisms.
              </p>
            </div>

            <div className="policy-section">
              <h2>8. Cookie Consent</h2>
              <p>
                When you first visit our website, we will ask for your consent to use non-essential cookies. You can change your cookie preferences at any time by:
              </p>
              <ul>
                <li>Using our cookie preference center (if available)</li>
                <li>Adjusting your browser settings</li>
                <li>Contacting us directly</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>9. Updates to This Cookie Policy</h2>
              <p>
                We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We encourage you to review this policy periodically to stay informed about how we use cookies.
              </p>
            </div>

            <div className="policy-section">
              <h2>10. More Information</h2>
              <p>
                For more information about how we handle your personal data, please see our <a href="/privacy-policy">Privacy Policy</a>.
              </p>
              <p>
                To learn more about cookies in general and how to manage them, visit:
              </p>
              <ul>
                <li><a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer">www.allaboutcookies.org</a></li>
                <li><a href="https://www.youronlinechoices.eu" target="_blank" rel="noopener noreferrer">www.youronlinechoices.eu</a></li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>11. Contact Us</h2>
              <p>If you have any questions about our use of cookies, please contact us:</p>
              <ul>
                <li><strong>Email:</strong> hello.dexova@gmail.com</li>
                <li><strong>Website:</strong> <a href="https://dexova.in" target="_blank" rel="noopener noreferrer">https://dexova.in</a></li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicyPage;
